"use client";

import { useEffect, useState } from "react";
import { useParams } from "next/navigation";
import PhoneInputBase from "react-phone-number-input";
import "react-phone-number-input/style.css";
import labelsPL from "react-phone-number-input/locale/pl.json";
import labelsEN from "react-phone-number-input/locale/en.json";
import labelsDE from "react-phone-number-input/locale/de.json";
import labelsFR from "react-phone-number-input/locale/fr.json";
import labelsES from "react-phone-number-input/locale/es.json";
import labelsIT from "react-phone-number-input/locale/it.json";
import labelsNL from "react-phone-number-input/locale/nl.json";
import labelsRU from "react-phone-number-input/locale/ru.json";
import styles from "./Form.module.css";
import phoneStyles from "./PhoneInput.module.css";
import { useFieldContext } from "./Field";

/* Locale map dla react-phone-number-input. Każdy plik to ~10KB JSON
   z nazwami krajów w danym języku. Default fallback = EN gdy locale
   spoza listy (lub brak [locale] w URL). Lista wybranych = najczęstsze
   języki EU + RU. Rozszerzaj wraz z potrzebami (lista dostępnych w
   `react-phone-number-input/locale/`). */
const LABELS_BY_LOCALE: Record<string, Record<string, string>> = {
  pl: labelsPL,
  en: labelsEN,
  de: labelsDE,
  fr: labelsFR,
  es: labelsES,
  it: labelsIT,
  nl: labelsNL,
  ru: labelsRU,
};

/**
 * PhoneInput — wrapper `react-phone-number-input` zintegrowany z Form primitives.
 *
 * **Co daje biblioteka:**
 *   - Country code selector (dropdown z flagą + nazwą)
 *   - Auto-format numeru per kraj (np. PL: +48 600 123 456)
 *   - Walidacja format (E.164)
 *   - Parsing z user input
 *
 * **Integracja z Field:** wrapper komponentu używa `useFieldContext` żeby
 * automatycznie dostać `aria-describedby`, `aria-invalid`. Native input
 * dostaje `id` z context'u przez `numberInputProps`. Stylizacja przez
 * `PhoneInput.module.css` — override default CSS biblioteki żeby pasowało
 * do brand tokenów (border, focus, colors).
 *
 * **Format value:** E.164 string (np. `+48600123456`) lub pusty string gdy
 * empty. Wartość w formState w tym formacie — backend musi przyjąć (lub
 * frontend konwertuje przed PATCH).
 *
 * **Default country:** Polska (`PL`). W przyszłości można podłączyć do
 * `domainConfig.defaultCountry`.
 *
 * **NIE używamy forwardRef** — `react-phone-number-input` ma własny ref
 * pattern (do component instance, nie HTMLInputElement). Konsumenci który
 * potrzebują ref'a do underlying input mogą użyć `numberInputProps.ref`.
 */
export function PhoneInput({
  value,
  onChange,
  invalid,
  disabled,
  className,
  defaultCountry = "PL",
}: PhoneInputProps) {
  const ctx = useFieldContext();
  const params = useParams();

  /* Locale z URL `[locale]` segment'u (np. `/pl/twoja-strona`). Może być
     undefined gdy komponent renderowany poza `[locale]` route tree —
     fallback do EN. */
  const rawLocale = Array.isArray(params?.locale)
    ? params.locale[0]
    : (params?.locale as string | undefined);
  const localeKey = (rawLocale ?? "").toLowerCase().slice(0, 2);
  const labels = LABELS_BY_LOCALE[localeKey] ?? labelsEN;

  /* SSR-safe defer — `react-phone-number-input` sortuje countries po
     localized label, ale internal sorting ma race condition między SSR a
     client hydration (różne ordering -> hydration mismatch dla <option>
     tekstów typu "Korea Południowa" vs "Korea Północna"). Render placeholder
     <input> na SSR + first paint, full PhoneInput dopiero po mount.
     Avoid'uje hydration mismatch warning + zachowuje deterministic layout. */
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const effectiveInvalid = invalid ?? ctx?.invalid ?? false;
  const wrapperClass = [
    phoneStyles.wrapper,
    effectiveInvalid ? styles.invalid : "",
    className,
  ].filter(Boolean).join(" ");

  /* Value przekazywana as-is — backend normalizuje do E.164 w obu kierunkach
     (`IAPI_userUpdate` write + `IAPI_user` read). Frontend tylko display.
     Empty -> undefined (library no-warning state).

     **Bez `international` prop** — input pokazuje format krajowy (np. "600 123
     456"), a kierunkowy (+48) wynika z flagi w selektorze. Value w stanie
     formularza i tak jest pełnym E.164 (`+48600123456`). User nie widzi `+48`
     duplikowanego raz na flagze, raz w input — UX czystsze.

     **`placeholder=" "`** — wymagane przez `:placeholder-shown` selector
     żeby floating label działał (Field context wstrzykuje label, label
     animuje gdy pole puste/wypełnione). Pusty space jest niewidoczny
     wizualnie (`.control::placeholder { color: transparent }`). */

  /* SSR placeholder — plain input (zachowuje layout, brak country selectora).
     Po mount client zastępuje pełnym PhoneInputBase. Bez tego warningi
     hydration mismatch i opcje countries renderują się nie po polsku. */
  if (!mounted) {
    return (
      <input
        type="tel"
        className={[styles.control, wrapperClass]
          .filter(Boolean)
          .join(" ")}
        id={ctx?.id}
        aria-describedby={ctx?.describedBy}
        aria-invalid={effectiveInvalid || undefined}
        aria-required={ctx?.required || undefined}
        placeholder={ctx?.inField ? " " : undefined}
        defaultValue={value ?? ""}
        disabled
      />
    );
  }

  return (
    <PhoneInputBase
      // @ts-expect-error — defaultCountry oczekuje literal CountryCode, my akceptujemy string
      defaultCountry={defaultCountry}
      labels={labels}
      value={value || undefined}
      onChange={(v) => onChange?.(v ?? "")}
      disabled={disabled}
      className={wrapperClass}
      numberInputProps={{
        className: styles.control,
        id: ctx?.id,
        placeholder: ctx?.inField ? " " : undefined,
        "aria-describedby": ctx?.describedBy,
        "aria-invalid": effectiveInvalid || undefined,
        "aria-required": ctx?.required || undefined,
      }}
    />
  );
}

export type PhoneInputProps = {
  value?: string;
  onChange?: (value: string) => void;
  invalid?: boolean;
  disabled?: boolean;
  className?: string;
  /** Country code (ISO 3166-1 alpha-2, np. "PL", "DE"). Default = "PL". */
  defaultCountry?: string;
};
